/**
 * StoryModeView — AI-generated business model narrative.
 *
 * Generates a compelling narrative from canvas data.
 * Includes loading skeleton, regenerate, and copy-to-clipboard.
 */

import { useEffect } from 'react'
import { BookOpen, RefreshCw, Copy, Check, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useState } from 'react'
import { useCanvasInteractive } from './CanvasInteractiveContext'
import { useCanvasStory } from '@/hooks/useCanvasStory'
import { useProjectStore } from '@/stores/projectStore'

export function StoryModeView() {
  const {
    interactiveConfig,
    sectionItems,
    canvasContext,
    selectedInstanceUri,
    accountId,
    projectId,
  } = useCanvasInteractive()

  const { currentProject } = useProjectStore()
  const [copied, setCopied] = useState(false)

  const { story, isLoading, error, generateStory, copyStory, hasData } = useCanvasStory({
    storyPromptTemplate: interactiveConfig.storyPromptTemplate,
    sectionItems,
    canvasContext,
    projectName: currentProject?.name,
    accountId,
    projectId,
    canvasInstanceUri: selectedInstanceUri,
  })

  // Auto-generate on mount if we have data
  useEffect(() => {
    if (hasData && !story && !isLoading) {
      generateStory()
    }
  }, [hasData])

  const handleCopy = async () => {
    await copyStory()
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (!hasData) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-16">
        <div className="w-12 h-12 rounded-full bg-warm-100 flex items-center justify-center mb-4">
          <BookOpen className="w-6 h-6 text-text-muted" />
        </div>
        <h3 className="text-lg font-display font-semibold text-text mb-2">
          No canvas data yet
        </h3>
        <p className="text-sm text-text-secondary text-center max-w-sm">
          Start filling in your canvas using the Canvas or Guided mode. Once you have data in a few
          sections, the story mode will generate a compelling narrative.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto">
        <div className="max-w-2xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center gap-2 mb-6">
            <BookOpen className="w-4 h-4 text-business" />
            <span className="text-xs font-medium text-business">Business Model Narrative</span>
          </div>

          {/* Story content */}
          {isLoading ? (
            <div className="space-y-3">
              {/* Loading skeleton */}
              <div className="h-4 bg-warm-100 rounded animate-pulse w-full" />
              <div className="h-4 bg-warm-100 rounded animate-pulse w-11/12" />
              <div className="h-4 bg-warm-100 rounded animate-pulse w-full" />
              <div className="h-4 bg-warm-100 rounded animate-pulse w-9/12" />
              <div className="h-4 bg-warm-100 rounded animate-pulse w-full" />
              <div className="h-4 bg-warm-100 rounded animate-pulse w-10/12" />
              <div className="h-4 bg-warm-100 rounded animate-pulse w-7/12" />
            </div>
          ) : error ? (
            <div className="text-sm text-red-600 p-4 bg-red-50 rounded-lg border border-red-200">
              {error}
            </div>
          ) : story ? (
            <blockquote
              className="text-base leading-relaxed text-text pl-5 border-l-3"
              style={{
                borderLeftColor: 'var(--color-business)',
                borderLeftWidth: '3px',
                fontFamily: 'Georgia, "Times New Roman", serif',
                lineHeight: 1.8,
              }}
            >
              {story}
            </blockquote>
          ) : null}

          {/* Actions */}
          {story && !isLoading && (
            <div className="flex items-center gap-2 mt-8">
              <Button
                variant="outline"
                size="sm"
                onClick={generateStory}
                className="text-xs gap-1.5"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Regenerate
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopy}
                className="text-xs gap-1.5"
              >
                {copied ? (
                  <>
                    <Check className="w-3.5 h-3.5 text-market" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" />
                    Copy to clipboard
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
